import Controller from '@ember/controller';
import { inject as service } from '@ember/service';
import { UserFlags } from 'cio/models/user';
import { DependentUserFlagFeature } from 'cio/components/experimental-user-flag-feature';
import AuthService from 'cio/services/auth';
import FeaturesService from 'ember-feature-flags/services/features';

interface UserFlagFeature {
  title: string;
  description: string;
  key?: keyof UserFlags;
  requiresFeedbackOnDisable?: boolean;
  dependentFlags?: DependentUserFlagFeature[];
  hasPermission: boolean;
}

export default class ExperimentalFeaturesController extends Controller {
  @service auth!: AuthService;
  @service features!: FeaturesService;

  get userFlagFeatures() {
    let flags: UserFlagFeature[] = [
      {
        title: 'Advanced Code Editor',
        description:
          'Use <a href="https://parcel.io/" target="_blank" rel="noopener noreferrer">Parcel</a> as your email code editor. Improve your email-crafting experience with features like syntax autocomplete, element inspect mode, and responsive previews.',
        key: 'parcel_editor_on',
        hasPermission:
          this.auth.hasPermission('journeys/broadcasts/edit') && this.auth.hasPermission('journeys/campaigns/edit'),
      },
      {
        title: 'AI Features',
        description:
          'Use AI features throughout Journeys and Data Pipelines to accelerate your workflow and improve your email content. Each feature must be enabled individually to gain access. Learn more about <a href="https://customer.io/legal/artificial-intelligence/" target="_blank" rel="noopener noreferrer">how we use AI</a> and <a href="https://customer.io/legal/privacy-policy/" target="_blank" rel="noopener noreferrer"/>our privacy policy</a>.',
        hasPermission:
          this.auth.hasPermission('journeys/broadcasts/edit') && this.auth.hasPermission('journeys/campaigns/edit'),
        dependentFlags: [
          {
            title: 'Email Content For Object Campaigns',
            description:
              'Show a button in the email composer that generates personalized email content using people, object, and relationship data.',
            key: 'show_objects_liquid_via_ai',
            hasPermission:
              this.auth.hasPermission('journeys/broadcasts/edit') && this.auth.hasPermission('journeys/campaigns/edit'),
          },
        ],
      },
      {
        title: 'Aggressive Positivity',
        description: 'Show a toggle in the workflow builder that enables some excitement when building workflows!',
        key: 'aggressive_positivity_available',
        hasPermission:
          this.auth.hasPermission('journeys/broadcasts/edit') && this.auth.hasPermission('journeys/campaigns/edit'),
      },
      {
        title: 'Composer Minimap',
        description:
          'Show a minimap overlay in the corner of a message composer to easily navigate between message actions in a workflow.',
        key: 'show_composer_minimap',
        hasPermission:
          this.auth.hasPermission('journeys/broadcasts/edit') && this.auth.hasPermission('journeys/campaigns/edit'),
      },
    ];

    if (this.features.isEnabled('parcel_amp') && this.auth.user?.userFlags.parcel_editor_on === true) {
      flags[0].dependentFlags = [
        {
          title: 'AMP for Email',
          description:
            'Enable the <a href="https://customer.io/docs/amp-for-email/" target="_blank" rel="noopener noreferrer">AMP for Email</a> tab in the Advanced Code Editor. Add and preview the AMP content with complete validation.',
          key: 'show_parcel_amp',
          hasPermission:
            this.auth.hasPermission('journeys/broadcasts/edit') && this.auth.hasPermission('journeys/campaigns/edit'),
        },
      ];
    }

    if (!this.features.isEnabled('ai_enabled')) {
      // If "ai_enabled" Feature Flag is disabled, then we remove the AI section completely. It defaults to: true
      flags.splice(1, 1);
    }

    return flags;
  }
}
